import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useLang } from '@/context/LanguageContext';
import { Heart, Crown, User } from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';

export const TopLikedWidget = ({ onSelect }) => {
  const { token, API } = useAuth();
  const { lang } = useLang();
  const [leaders, setLeaders] = useState([]);
  const [month, setMonth] = useState('');
  const [category, setCategory] = useState(null);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/members/leaderboard`, { headers });
        if (!active) return;
        setLeaders(res.data.leaders || []);
        setMonth(res.data.month || '');
        setCategory(res.data.category || null);
      } catch (err) {
        // Widget is optional — log once, never block the page
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.warn('[TopLikedWidget] failed to load leaderboard:', err?.message);
        }
      }
    })();
    return () => { active = false; };
  }, [API, headers]);

  if (!leaders.length) return null;

  const getPhoto = (p) => p?.photos?.find(ph => ph) || '';
  const getRankColor = (i) => ['#F59E0B', '#94A3B8', '#B45309', '#C7867A'][i] || '#C7867A';

  return (
    <motion.div
      initial={{ opacity: 0, y: -5 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="px-4 sm:px-6 pt-4"
      data-testid="top-liked-widget"
    >
      <div className="glass-card rounded-2xl px-4 py-3" style={{ border: '1px solid var(--border-color)' }}>
        <div className="flex items-center gap-2 mb-2.5">
          <Crown className="w-4 h-4 text-[#F59E0B]" />
          <h2 className="t-text font-semibold text-xs sm:text-sm">
            {lang === 'en' ? 'Top Liked This Month' : 'Paling Disukai Bulan Ini'}
          </h2>
          {month && <span className="t-text-muted text-[10px] ml-auto">{month}</span>}
          {category && <span className="text-[9px] bg-[#D98A94]/10 text-[#D98A94] px-2 py-0.5 rounded-full uppercase tracking-wider">{category}</span>}
        </div>
        <div className="flex gap-2 sm:gap-3 overflow-x-auto scrollbar-hide pb-1" style={{ scrollbarWidth: 'none' }}>
          {leaders.map((p, i) => {
            const photo = getPhoto(p);
            const accent = getRankColor(i);
            return (
              <button
                key={p.id}
                onClick={() => onSelect && onSelect(p)}
                data-testid={`leaderboard-rank-${i + 1}`}
                className="flex-shrink-0 flex items-center gap-2 pl-1.5 pr-3 py-1.5 rounded-full glass hover:bg-[var(--bg-input)] transition active:scale-95"
                style={{ border: `1px solid ${accent}30` }}
              >
                <div className="relative w-7 h-7 rounded-full overflow-hidden flex-shrink-0" style={{ border: `2px solid ${accent}` }}>
                  {photo ? (
                    <img src={photo} alt="" className="w-full h-full object-cover" loading="lazy" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-[var(--bg-input)]">
                      <User className="w-3.5 h-3.5 t-text-muted" />
                    </div>
                  )}
                  <span
                    className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full text-[8px] font-black text-white flex items-center justify-center"
                    style={{ backgroundColor: accent }}
                  >
                    {i + 1}
                  </span>
                </div>
                <span className="t-text text-xs font-medium truncate max-w-[80px]">
                  {p.name || (lang === 'en' ? 'Member' : 'Anggota')}
                </span>
                <div className="flex items-center gap-0.5 text-[#D98A94]">
                  <Heart className="w-3 h-3 fill-[#D98A94]" />
                  <span className="text-[10px] font-bold">{p.monthly_likes || 0}</span>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </motion.div>
  );
};
